import { z } from 'zod';

import { DashboardWidgetWidth } from '@/components/dashboard/model';
import { dashboardDateScopeSchema } from '../../../dashboard/model';

export enum IntegrationDashboardWidgetType {
  Metric = 'INTEGRATION_METRIC',
  PerformanceChart = 'INTEGRATION_PERFORMANCE_CHART',
}

export enum IntegrationDashboardMetric {
  BidRequests = 'BID_REQUESTS',
  BidResponses = 'BID_RESPONSES',
  WonBids = 'WON_BIDS',
  FilledImpressions = 'FILLED_IMPRESSIONS',
  AdImpressions = 'AD_IMPRESSIONS',
  Revenue = 'REVENUE',
  Profit = 'PROFIT',
  Margin = 'MARGIN',
  Ecpm = 'ECPM',
  Srpm = 'SRPM',
}

const widgetBase = {
  id: z.string().trim().min(1),
  width: z.enum(DashboardWidgetWidth),
  title: z.string().trim().min(1).optional(),
  dateScope: dashboardDateScopeSchema,
};

export const integrationMetricWidgetSchema = z.object({
  ...widgetBase,
  type: z.literal(IntegrationDashboardWidgetType.Metric),
  metric: z.enum(IntegrationDashboardMetric),
});

export const integrationPerformanceChartWidgetSchema = z.object({
  ...widgetBase,
  type: z.literal(IntegrationDashboardWidgetType.PerformanceChart),
  metrics: z.array(z.enum(IntegrationDashboardMetric)).min(1),
});

export const integrationDashboardWidgetSchema = z.discriminatedUnion('type', [
  integrationMetricWidgetSchema,
  integrationPerformanceChartWidgetSchema,
]);

export const integrationDashboardDocumentSchema = z.object({
  version: z.literal(1),
  widgets: z.array(integrationDashboardWidgetSchema).superRefine((widgets, context) => {
    const ids = new Set<string>();
    widgets.forEach((widget, index) => {
      if (ids.has(widget.id)) {
        context.addIssue({
          code: 'custom',
          message: 'Integration dashboard widget IDs must be unique.',
          path: [index, 'id'],
        });
      }
      ids.add(widget.id);
    });
  }),
});

export type IntegrationDashboardWidget = z.infer<typeof integrationDashboardWidgetSchema>;
export type IntegrationMetricWidget = z.infer<typeof integrationMetricWidgetSchema>;
export type IntegrationPerformanceChartWidget = z.infer<
  typeof integrationPerformanceChartWidgetSchema
>;
export type IntegrationDashboardDocument = z.infer<typeof integrationDashboardDocumentSchema>;
